<template>
  <div class="content">
    <h1>Jahreskalender und Urlaubsplan {{ currentYear }}</h1>

    <div>
      <div class="flexrow-item">
        <span>Jahr:</span>
        <input
          class="year-field"
          :max="realYear+15"
          :min="realYear-12"
          v-model="currentYear"
          type="number"
          step="1"
          placeholder="Jahr..."
        >
      </div>

      <div class="flexrow-item">
        <input
          class="search-field"
          v-model="filter"
          type="text"
          id="employee-filter"
          placeholder="Mitarbeiter suchen..."
        />
      </div>
    </div>
    <!-- Mitarbeiterinformationen -->
    <section id="employee-info">
      <table class="employee-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Antrag</th> <!-- Neue Spalte für Antrag -->
            <th>Name</th>
            <th>Personalnr.</th>
            <th>Jahresanspruch</th>
            <th>Überstundentage</th>
            <th>Tage Gesamt</th>
            <th>Genommene Tage</th>
            <th>Verbleibende Tage</th>
            <th>Krankheitstage</th>
          </tr>
        </thead>
        <tbody id="employee-rows" :key="incnum">
          <tr v-for="(person, i) in employees" v-show="person.name.toLowerCase().includes(filter.toLowerCase())">
            <td>{{ i + 1 }}</td>
            <td class="antrag" @click="onFormClick(person)">{{ person.day_off_requests[currentYear] }}</td>
            <td class="employee-name column-2">{{ person.name }}</td>
            <td
              contenteditable="true"
              spellcheck="false"
              @keydown="event => onDataNav(event, i, 0)"
              @input="onChangeUserData(person)"
              @focus="selectOnFocus"
              :ref="`${person.id}-id`"
              class="employee-id"
            >
              {{ person.emp_id_cpy }}
            </td>
            <td
              contenteditable="true"
              spellcheck="false"
              @keydown="event => onDataNav(event, i, 1)"
              @input="onChangeYearly(person)"
              @focus="selectOnFocus"
              :ref="`${person.id}-year`"
              class="available-days"
            >
              {{ person.yearly_cpy }}
            </td>
            <td class="overtime-hours" :ref="`${person.id}-tot-o`">{{ person.o_total }}</td>
            <td class="total-days" :ref="`${person.id}-tot`">{{ person.total }}</td>
            <td class="taken-days" :ref="`${person.id}-tot-u`">{{ person.u_total }}</td>
            <td class="remaining-days" :ref="`${person.id}-rem`">{{ person.remaining }}</td>
            <td class="sick-days" :ref="`${person.id}-tot-k`">{{ person.k_total }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <!-- Monatsansichten untereinander -->
    <section class="calendar">
      <div id="monthly-calendars">
        <div class="flexrow month-tabs">
          <div
            :class="{
              'tab': true,
              'flexrow-item': true,
              'active-tab': month===currentTab
            }"
            v-for="(month, i) in monthNames"
            @click="currentTab = month"
          >
            {{ (i+1) + ' - ' + month }}
          </div>
        </div>
        <div class="month-title">
          {{ currentTab }}
        </div>
        <div class="month" v-for="(month, i) in monthNames" v-show="currentTab === month">
          <table class="month-table">
            <tr>
              <td></td>
              <td class="column-2"></td>
              <th colspan="2"></th>
              <th class="sum-o"></th>
              <th
                v-for="(weekday, d) in getWeekdays(i)"
                :class="{ 'school-s-out': school_holidays[`${currentYear}-${pad(i+1)}-${pad(d+1)}`] }"
              >
                {{ weekday }}
              </th>
            </tr>
            <tr>
              <td></td>
              <td class="column-2"></td>
              <th class="sum-k">K</th>
              <th class="sum-u">U</th>
              <th class="sum-o">Ü</th>
              <th
                :class="{
                  today: currentDay === day && currentMonth === month && currentYear === realYear
                }" 
                v-for="day in daysInMonth[i]"
              >
                {{ day }}
              </th>
            </tr>
            <tr v-for="(person, pi) in employees" v-show="isPersonDisplayed(person)">
              <td class="column-1">{{ pi + 1 }}</td>
              <td class="column-2 calendar-employee-name">{{ person.name }}</td>
              <td class="sum-k" :ref="`${person.id}-${pad(i+1)}-sum-k`">0</td>
              <td class="sum-u" :ref="`${person.id}-${pad(i+1)}-sum-u`">0</td>
              <td class="sum-o" :ref="`${person.id}-${pad(i+1)}-sum-o`">0</td>
              <td
                contenteditable="true"
                spellcheck="false"
                @keydown="event => onCalendarNav(event, pi, i+1, day)"
                @input="event => onDayOffSet(event, person, i+1, day)"
                @focus="selectOnFocus"
                v-for="day in daysInMonth[i]"
                :ref="`${person.id}-${pad(i+1)}-${pad(day)}`"
                :class="{
                  today: currentDay === day && currentMonth === month && currentYear === realYear,
                  weekend: isWeekend(day, i+1, person),
                  holiday: isHoliday(day, i+1),
                  birthday: isBirbday(day, i+1, person)
                }"
              ></td>
            </tr>
          </table>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import client from '@/store/api/client'

export default {
  name: 'holidays',

  data () {
    return {
      filter: '',
      incnum: 0,
      currentYear: 2025,
      realYear: 2025,
      currentDay: 1,
      currentMonth: 'Januar',
      currentTab: 'Januar',
      monthNames: ["Januar", "Februar", "März", "April", "Mai", "Juni",
                  "Juli", "August", "September", "Oktober", "November", "Dezember"],
      daysInMonth: [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31],
      weekDays: ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"],
      holidays: [
        '2025-01-01',   // Neujahr (1. Januar)
        '2025-05-01',   // Maifeiertag (1. Mai)
        '2025-10-03',   // Tag der Deutschen Einheit (3. Oktober)
        '2025-12-25', // Weihnachten (25. Dezember)
        '2025-12-26'  // 2. Weihnachtsfeiertag (26. Dezember)
      ],
      school_holidays: {},
      employees: [],
      daysOff: {},
    }
  },

  computed: {
    ...mapGetters([
      'people',
    ]),
  },

  methods: {
    ...mapActions([
      'addDummyDayOffs',
      'getRawDaysOff',
      'getSingleDayOff',
      'removeDayOff',
      'requestDayOff',
      'loadPeople',
      'setDayOff',
      'editPerson'
    ]),

    isPersonDisplayed (person) {
      return person.name.toLowerCase().includes(this.filter.toLowerCase())
    },

    onDataNav(event, pers_ind, col) {
      const columns = ['id', 'year']
      let nextCoords = [pers_ind, col]
      switch (event.key) {
        case "Enter":
          event.preventDefault()
        case "ArrowDown":
          do {
            nextCoords[0] += 1
            if (nextCoords[0] >= this.employees.length) { return }
          } while (!this.isPersonDisplayed(this.employees[nextCoords[0]]))
          break;
        case "ArrowUp":
          do {
            nextCoords[0] -= 1
            if (nextCoords[0] < 0) { return }
          } while (!this.isPersonDisplayed(this.employees[nextCoords[0]]))
          break;
        case "ArrowRight":
          nextCoords[1] += 1
          if (nextCoords[1] > 1) { return }
          break;
        case "ArrowLeft":
          nextCoords[1] -= 1
          if (nextCoords[1] < 0) { return }
          break;
        default:
          return
      }
      const td = this.$refs[`${this.employees[nextCoords[0]].id}-${columns[nextCoords[1]]}`][0]
      td.focus()
      setTimeout(() => {
        window.getSelection().selectAllChildren(td)
      }, 100)
    },

    onCalendarNav (event, pers_ind, month, day) {
      let nextCoords = [pers_ind, month, day]
      switch (event.key) {
        case "Enter":
          event.preventDefault()
        case "ArrowDown":
          do {
            nextCoords[0] += 1
            if (nextCoords[0] >= this.employees.length) { return }
          } while (!this.isPersonDisplayed(this.employees[nextCoords[0]]))
          break;
        case "ArrowUp":
          do {
            nextCoords[0] -= 1
            if (nextCoords[0] < 0) { return }
          } while (!this.isPersonDisplayed(this.employees[nextCoords[0]]))
          break;
        case "ArrowRight":
          nextCoords[2] += 1
          if (nextCoords[2] > this.daysInMonth[month-1]) { return }
          break;
        case "ArrowLeft":
          nextCoords[2] -= 1
          if (nextCoords[2] < 1) { return }
          break;
        default:
          return
      }
      const td = this.$refs[`${this.employees[nextCoords[0]].id}-${this.pad(nextCoords[1])}-${this.pad(nextCoords[2])}`][0]
      td.focus()
      setTimeout(() => {
        window.getSelection().selectAllChildren(td)
      }, 100)
    },

    pad (number) {
      return String(number).padStart(2,'0')
    },

    selectOnFocus (event) {
      window.getSelection().selectAllChildren(event.target)
    },

    toDaysString (num) {
      if (num === null) { return ''} 
      return num.toString() + (num === 1 ? ' Tag' : ' Tage')
    },

    onFormClick (person) {
      const win = window.open(`${window.location.origin}/urlaubsantrag.html`, 'Urlaubsantrag', 'hieght=600,width=1000')
      setTimeout(() => {
        win.postMessage({
          employee_id: person.employee_id || '',
          form_nr: person.day_off_requests[this.currentYear] + 1,
          name: person.name || '',
          yearly: this.toDaysString(person.yearly_holidays),
          overtime: this.toDaysString(person.o_total),
          total: this.toDaysString(person.total),
          taken: this.toDaysString(person.u_total),
          remaining: this.toDaysString(person.remaining)
        })
      }, 700)
      win.addEventListener('message', ({data}) => {
        if (data.saved) {
          this.requestDayOff({person_id: person.id, year: this.currentYear}).then(() => {
            this.incnum +=1
          })
        }
      })
      win.focus()
    },

    onChangeUserData (person) {
      person.employee_id = this.$refs[`${person.id}-id`][0].textContent.trim()
      this.editPerson(person)
    },

    onChangeYearly (person) {
      person.yearly_holidays = Number(this.$refs[`${person.id}-year`][0].textContent)
      this.editPerson(person)
      person.total = person.yearly_holidays + person.o_total
      person.remaining = person.total - person.u_total
    },

    updateUKSums (person, month) {
      let k_sum = 0
      let u_sum = 0
      let o_sum = 0
      for (let day = 1; day <= this.daysInMonth[month-1]; day++) {
        const val = this.$refs[`${person.id}-${this.pad(month)}-${this.pad(day)}`][0].textContent.toLowerCase()
        if (val === 'ü') {
          o_sum += 1
        } else if ( !(this.isWeekend(day, month, person) || this.isHoliday(day, month)) ) {
          if (val === 'k') {
            k_sum += 1
          } else if (Number(val) > 0) {
            u_sum += Number(val)
          }
        }
      }
      const k_cell = this.$refs[`${person.id}-${this.pad(month)}-sum-k`][0]
      const u_cell = this.$refs[`${person.id}-${this.pad(month)}-sum-u`][0]
      const o_cell = this.$refs[`${person.id}-${this.pad(month)}-sum-o`][0]
      person.k_total += k_sum - Number(k_cell.textContent)
      person.u_total += u_sum - Number(u_cell.textContent)
      person.o_total += o_sum - Number(o_cell.textContent)
      person.total = person.yearly_holidays + person.o_total
      person.remaining = person.total - person.u_total
      k_cell.textContent = k_sum
      u_cell.textContent = u_sum
      o_cell.textContent = o_sum
    },

    loadOffDays () {
      for (let m = 1; m <= 12; m++) {
        this.daysOff[m] = []
        this.getRawDaysOff({
          year: this.currentYear,
          month: m,
        }).then(res => {
          this.daysOff[m] = res
          for (const pers of this.employees) {
            for (let day = 1; day <= this.daysInMonth[m-1]; day++) {
              const td = this.$refs[`${pers.id}-${this.pad(m)}-${this.pad(day)}`][0]
              td.innerHTML = ''
              td.style['background-color'] = td.classList.contains('today') ? '#d5a9ff' : null
            }
            const finds = this.daysOff[m].filter(x => x.person_id === pers.id)
            for (const f of finds) {
              if (f.date.substring(0, 4) === this.currentYear.toString()) {
                const td = this.$refs[`${pers.id}-${f.date.substring(5)}`]
                const val = f.value || 1
                if (td && td[0] && val !== '0') {
                  td[0].innerHTML = val
                  this.updateCellColor(td[0])
                }
              }
            }
            this.updateUKSums(pers, m)
          }
        })
      }
    },

    onDayOffSet (event, person, month, day) {
      if (event.data === '0' || event.data === '.') {return}
      this.updateUKSums(person, month)
      this.updateCellColor(event.target)
      const date = `${this.currentYear}-${this.pad(month)}-${this.pad(day)}`
      this.removeDayOff({person_id: person.id, date})
      if (event.data) {
        this.setDayOff({
          personId: person.id,
          date,
          value: event.target.textContent.toUpperCase().trim(),
        })
      }
    },

    updateCellColor (cell) {
      const value = cell.textContent.toLowerCase()
      if (value === 'ü') {
        cell.style['background-color'] = '#ffcc99'
      } else if (value === 'k') {
        cell.style['background-color'] = '#87cefa'
      } else if (Number(value) > 0) {
        cell.style['background-color'] = '#a0e7a0'
      } else { 
        cell.style['background-color'] = cell.classList.contains('today') ? '#d5a9ff' : null
      }
    },

    getWeekdays (monthInd) {
      const res = []
      const firstDayOfWeek = new Date(this.currentYear, monthInd, 1).getDay();
      const offset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
      for (let day = 1; day <= this.daysInMonth[monthInd]; day++) {
        res.push(this.weekDays[(offset + day - 1) % 7])
      }
      return res
    },

    isHoliday (day, month) {
      return this.holidays.some(holiday => holiday === `${this.currentYear}-${this.pad(month)}-${this.pad(day)}`)
    },

    isWeekend (day, month, person) {
      const wd = this.getWeekdays(month-1)[day - 1]
      return wd === 'Sa' || wd === 'So' || (wd === 'Fr' && person.name === "Malte Jensen");
    },

    isBirbday (day, month, person) {
      if (!person.birthday) { return false }
      const birbday = new Date(person.birthday)
      return birbday.getDate() === day && birbday.getMonth() + 1 === month
    },

    updateYear () {
      this.addDummyDayOffs(this.currentYear)
      this.loadOffDays()
      client.pget(`https://digidates.de/api/v1/germanpublicholidays?year=${this.currentYear}&region=de-hh`).then(res => {
        this.holidays = Object.keys(res)
      })
      for (let h in this.school_holidays) {
        h = false
      }
      const offs = this.currentYear - this.realYear
      if (offs < 4 && offs > -4) {
        client.pget(`https://schulferien-api.de/api/v2/${this.currentYear}?states=hh`).then(res => {
          for (const day of res) {
            const start = new Date(day.start);
            const end = new Date(day.end);
            while (start <= end) {
              this.school_holidays[start.toISOString().substring(0,10)] = true
              start.setDate(start.getDate() + 1)
            }
          }
        })
      }
    }
  },

  watch: {
    currentYear () {
      this.updateYear()
    }
  },

  mounted () {
    const today = new Date()
    this.currentTab = this.monthNames[today.getMonth()]
    this.currentMonth = this.monthNames[today.getMonth()]
    this.currentDay = today.getDate()
    this.loadPeople(() => {
      this.employees = this.people.filter(p => p.active && p.role !== 'client' && p.role !== 'vendor')
        .map(p => {
          return {
            ...p,
            emp_id_cpy: p.employee_id,
            yearly_cpy: p.yearly_holidays,
            day_off_requests: 'day_off_requests' in p ? p.day_off_requests : {},
            o_total: 0,
            u_total: 0,
            k_total: 0,
            total: 0,
            remaining: 20,
          }
        }).sort((a, b) => {
          if (!a.employee_id && !b.employee_id) { return a.name.localeCompare(b.name) }
          else if (!a.employee_id) { return 1 }
          else if (!b.employee_id) { return -1 }
          const aNum = Number(a.employee_id)
          const bNum = Number(b.employee_id)
          if (!aNum && !bNum) { return a.employee_id.localeCompare(b.employee_id) }
          else if (!aNum) { return 1 }
          else if (!bNum) { return -1 }
          else if (aNum < bNum) { return -1 }
          else if (aNum > bNum) { return 1 }
          return a.name.localeCompare(b.name)
        })
      this.realYear = today.getFullYear()
      this.currentYear = this.realYear
      this.updateYear()
    })
  }
}
</script>

<style lang="css" scoped>/* Grundlegendes Styling */

.content {
  font-family: Arial, sans-serif;
  padding: 0;
  background-color: #f4f4f4;
  margin-right: 0;
  width: auto;
  margin-top: 60px;
}

header {
  background-color: #333;
  color: #fff;
  padding: 1em 0;
  text-align: center;
}

tr {
  height: 38px;
}

td, th {
  padding: 0;
  text-align: center;
  vertical-align: center;
  max-height: 38px;
}

h1, h2 {
  color: #333;
  text-align: center;
  border: none;
}

footer {
  text-align: center;
  padding: 1em 0;
  background-color: #333;
  color: #fff;
}

table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

/* Tabellen Styling für Mitarbeiterblock */
.employee-table {
  border-collapse: collapse;
  table-layout: auto;
  margin-bottom: 1em;
  border-bottom: 1px solid black;
}

.employee-table th, .employee-table td {
  border: 1px solid #ccc;
  text-align: center;
}

/* Spaltenbreiten für Mitarbeiterblock */
.employee-table .column-1, .month-table .column-1 {
  width: 5%; /* Beispielbreite für die erste Spalte */
}

.employee-table .column-2, .month-table .column-2 {
  width: 20%; /* Beispielbreite für die zweite Spalte */
}

/* Monatsüberschriften Styling */
.month-title {
  background-color: #d3d3d3;
  padding-bottom: 1px;
  text-align: center;
  font-size: 20pt;
  font-weight: bold;
  padding: 0.2em;
}

.month-tabs {
  background: #a1a1a1;
  margin: 0;
}
.tab {
  cursor: pointer;
  color: rgb(255, 255, 255);
  width: 8em;
  font-size: 11pt;
  border: 1px solid black;
  border-top: none;
  border-left: none;
  background: #a1a1a1;
  margin: 0;
  text-align: center;
}
.tab:hover {
  background: #d3d3d3;
}
.active-tab {
  background: #d3d3d3;
  border-bottom: none;
  color: rgb(97, 97, 97);
}

.month-table th, .month-table td {
  border: 1px solid #ccc;
  padding: 0.5em;
  text-align: center;
}

th {
  background-color: #00000010;
  font-weight: bold;
}

.school-s-out {
  background-color: #002fff;
  color: white;
}

/* Wochentagszellen und Datumszellen */
.month-table .empty {
  background-color: #f4f4f4;
}
/* Hover-Effekt für editierbare Zellen */
td[contenteditable="true"]:hover {
  border: 3px solid blue;
}

td[contenteditable="true"]:focus {
  border: 3px solid green;
}
tr:has(td[contenteditable="true"]:hover) {
  border: 3px solid rgba(49, 0, 128, 0.4);
}
tr:has(td[contenteditable="true"]:focus) {
  border: 3px solid rgba(0, 141, 47, 0.4);
}

td[contenteditable="true"] {
  white-space: nowrap;
  overflow: hidden;
  background-color: #fff;
  cursor: pointer;
  border: 1px solid #ccc;
}

/* Farbmarkierungen für Summenspalten */
.filled {
  background-color: #87cefa; /* Blau eingefärbt, wenn der Wert größer als 0 ist */
}

.filled-green {
  background-color: #a0e7a0; /* Grüner Farbton für die Summe der Urlaubstage */
}

-entries.u-filled {
  background-color: #ffcc99; /* Orange für Überstunden */
}

.sum-o {
  display: none;
}

/* Feiertage und Wochenenden im Kalender */
.month-table .holiday {
  background-color: #ffdddd; /* Rot für Feiertage */
}

.month-table .weekend {
  background-color: #f0f0f0; /* Hellgrau für Wochenenden */
}

.month-table .birthday {
  image-rendering: pixelated;
  background-image: url('../../assets/heart.png');
  background-repeat: no-repeat;
  background-size: contain;
  background-position: 50% 50%;
  color: white;
}

/* Farbmarkierung für Mitarbeiterblock Summenspalten */
.sick-days {
  background-color: #87cefa;
}

.overtime-hours {
  background-color: #ffcc99;
}

.remaining-days {
  background-color: #a0e7a0;
}

/* Filterfeld Styling */
#employee-filter {
  width: 100%;
  padding: 0.5em;
  margin-top: 0.5em;
  font-size: 1em;
}
/* Mauszeiger für die Zellen der "Antrag"-Spalte ändern */
.antrag {
  cursor: pointer;
}

.year-field {
  margin-left: 1em;
  margin-top: 0.5em;
  width: 5em;
}

.today {
  background: #d5a9ff;
}
</style>